import { createSelector } from "@reduxjs/toolkit"
import { get, memoize } from "nodash"
import {
    selectInfraredFlightById,
    selectInfraredFlights,
    selectRawInfraredImagesByFlightId,
} from "../../flights/selectors"
import {
    InfraredImageState,
    selectAllUploads,
    selectUploadsForSet as selectInfraredUploadsForSet,
} from "plugins/InfraredImage/InfraredImageSlice"
import {
    GenericUpload,
    ID,
    ImagePreviewType,
    ImageProcessingState,
    ImageWarnings,
    InfraredImageWithContext,
    isUsable,
    UploadStateSummary,
} from "@nova/constants"
import { selectActivatedFireId } from "features/map/control/selectors"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import {
    getInfraredImageS3Key,
    getSampleInfraredImageS3Key,
} from "features/data-management/hotspots/editor/infrared-image/module"
import store from "integrations/redux/store"
import { getFileNameFromImageId, UploadState } from "@nova/slice-multifile-upload"
import { infraredWarningProcessor } from "plugins/InfraredImage/InfraredImageWarnings"
import { createGenericSidePanelStateSelector } from "panels/content/side-panel/selectors"
import { InfraredFlight } from "@nova/protobuf"
import { RootState } from "integrations/redux"
import { getInfraredDownscaledImageS3Key } from "@nova/util-s3"

const selectInfraredFlightDemoIdByFlightId = memoize((id: string) =>
    createSelector(selectInfraredFlightById(id), (flight) => flight?.demoId)
)

export const selectInfraredImagesByFlightId = memoize((id: string) =>
    createSelector(
        [
            selectRawInfraredImagesByFlightId(id),
            selectActiveWorkspaceId(),
            selectActivatedFireId,
            selectInfraredFlightDemoIdByFlightId(id),
        ],
        (images, orgId, projectId, demoId) => {
            if (!orgId || !projectId) {
                return []
            }

            const isDemo = !!demoId

            return images.map<InfraredImageWithContext>((image) => ({
                ...image,
                s3Key: isDemo
                    ? getSampleInfraredImageS3Key(demoId, image.id)
                    : getInfraredImageS3Key(orgId, projectId, id, image.id),
                s3KeyLR: image.hasLowQuality
                    ? getInfraredDownscaledImageS3Key({
                          orgId,
                          projectId,
                          flightId: id,
                          fileId: image.id,
                      })
                    : undefined,
                flightId: id,
                fileName: getFileNameFromImageId(image.id),
                state: ImageProcessingState.IngestDone,
            }))
        }
    )
)

export const selectAllInfraredFlights = (state: RootState) => root(state)

export const selectInfraredImagesContextMap = createSelector(
    [selectAllInfraredFlights],
    (flights) => {
        const state = store.getState()
        const imageMap = {} as Record<string, InfraredImageWithContext>
        flights.forEach((flight) => {
            selectInfraredImagesByFlightId(flight.id)(state).forEach(
                (image) => (imageMap[image.id] = image)
            )
        })
        return imageMap
    }
)

export const selectInfraredImageById = memoize((id: string) =>
    createSelector([selectAllUploads(), selectInfraredImagesContextMap], (uploads, images) => {
        const upload = uploads[id]
        const cached = images[id]
        if (cached) {
            if (!upload) return cached
            const uploadError = get(upload, "error")
            return {
                ...cached,
                state: get(upload, "state"),
                ...(uploadError !== undefined ? { error: uploadError } : {}),
            }
        }
        return upload
    })
)

const mergeImagesAndUploads = (
    images: InfraredImageWithContext[],
    uploads: UploadState<InfraredImageState>[] | undefined
): (UploadState<InfraredImageState> | InfraredImageWithContext)[] => {
    const uploadById = new Map(uploads?.map((u) => [u.id, u]))
    const res = [] as (UploadState<InfraredImageState> | InfraredImageWithContext)[]
    images.forEach((image) => {
        const activeUpload = uploadById.get(image.id)
        if (activeUpload) {
            // Active upload session: use upload-slice state so entity-cache websocket
            // updates don't prematurely show IngestDone before the ingest signal fires
            const infraredUploadError = get(activeUpload, "error")
            res.push({
                ...image,
                state: get(activeUpload, "state"),
                ...(infraredUploadError !== undefined ? { error: infraredUploadError } : {}),
            } as unknown as InfraredImageWithContext)
            uploadById.delete(image.id)
        } else {
            // Refresh: image is entity-cached with no active upload, ingest is done
            res.push({ ...image, state: ImageProcessingState.IngestDone })
        }
    })
    uploadById.forEach((upload) => res.push(upload))
    return res
}

export const selectInfraredImagesAndUploadsByFlightId = memoize((id: string) =>
    createSelector(
        [selectInfraredImagesByFlightId(id), selectInfraredUploadsForSet(id)],
        (images, uploads) => mergeImagesAndUploads(images, uploads)
    )
)

export const selectInfraredImagesAndUploadsForManyFlightIds = memoize((ids: ID[]) =>
    createSelector(
        ids.map((id) => selectInfraredImagesAndUploadsByFlightId(id)),
        (...allResults) => {
            const flattened = allResults.flatMap((result) => result ?? [])
            return flattened.length > 0 ? flattened : undefined
        }
    )
)
export const selectInfraredImagesAndUploadsWithContextByFlightId = memoize((id: string) =>
    createSelector(
        [selectInfraredImagesByFlightId(id), selectInfraredUploadsForSet(id)],
        (images, uploads) => {
            let imageColors: Record<ID, string> = {}
            if (
                uploads?.every(
                    (upload) =>
                        (upload as GenericUpload).state === ImageProcessingState.ProcessingDone
                )
            ) {
                imageColors = infraredWarningProcessor(id, uploads as GenericUpload[])
            }
            return mergeImagesAndUploads(images, uploads).map((upload) => ({
                ...upload,
                warningColor: imageColors[upload.id],
                imagePreviewType: ImagePreviewType.Infrared,
            }))
        }
    )
)

export const selectSummaryFromInfraredUploads = memoize((id: string) =>
    createSelector([selectInfraredImagesAndUploadsByFlightId(id)], (uploads) => {
        const summary: UploadStateSummary = {
            stateCount: {} as Record<ImageProcessingState, number>,
            uploadCount: uploads?.length || 0,
            errorCount: 0,
            warnings: {} as ImageWarnings,
        }
        uploads?.forEach((upload) => {
            const usable = isUsable(upload)
            if (!usable) return summary.errorCount++
            const state = "state" in usable ? usable.state : ImageProcessingState.IngestDone
            summary.stateCount[state] = ++summary.stateCount[state] || 1
        })
        return summary
    })
)

export const selectInfraredUploadSize = memoize((id: string) =>
    createSelector([selectInfraredImagesAndUploadsByFlightId(id)], (uploads) => {
        let totalSize = 0
        uploads?.forEach((upload) => {
            const size = get(upload, "size", 0)
            totalSize += size
        })
        return totalSize
    })
)

export const selectInfraredFinishedUploadSize = memoize((id: string) =>
    createSelector([selectInfraredImagesAndUploadsByFlightId(id)], (uploads) => {
        let totalSize = 0
        uploads?.forEach((upload) => {
            const state = get(upload, "state")
            const size = get(upload, "size", 0)
            if (state !== ImageProcessingState.IngestDone) return
            totalSize += size
        })
        return totalSize
    })
)

export const selectIsInfraredFlightUploading = memoize((id: string) =>
    createSelector([selectSummaryFromInfraredUploads(id)], (summary) => {
        return (
            !!summary.stateCount?.[ImageProcessingState.PendingUpload] ||
            !!summary.stateCount?.[ImageProcessingState.Uploading] ||
            !!summary.stateCount?.[ImageProcessingState.UploadDone]
        )
    })
)

const root = (state: RootState) => state.project.data.infraredFlights

export const selectSidePanelHotspotData = createGenericSidePanelStateSelector(
    InfraredFlight.create,
    selectInfraredFlightById,
    selectAllInfraredFlights
)

export const selectHotspotFlightIdsForMergedFlightById = memoize((id: string) =>
    createSelector([selectInfraredFlights], (infraredFlights) =>
        infraredFlights.filter((flight) => flight.mergeId === id).map((flight) => flight.id)
    )
)
